/**
 * Step 5 (Platform-Eng workflow) — AddExistingSkillSlideout.
 *
 * <p>Closes the Tier-3 gap: skills today are agent-scoped at
 * creation, and there's no UI for binding a pre-existing skill
 * (from the tenant's shared skill registry catalog) to the current
 * agent. For a platform team running a shared skill library that's
 * the wrong shape — every team had to re-author skills they could
 * have reused. This slideout adds the "Add existing skill" affordance
 * the BuildTab toolbar mounts next to the canonical "Add skill"
 * button.</p>
 *
 * <h3>Wiring</h3>
 *
 * <p>The backend already exposes everything we need:</p>
 * <ul>
 *   <li>{@code GET /api/v1/skill-registry} — paginated marketplace</li>
 *   <li>{@code POST /api/v1/skill-registry/search} — semantic + text</li>
 *   <li>{@code POST /api/v1/skill-registry/{id}/install} — install with
 *       {@code agentId} body field materialises an AUTO_REGISTRY_INSTALL
 *       Skill row on the agent so it's immediately invocable. This is
 *       the operation Step 5 needed; we don't add a new endpoint.</li>
 * </ul>
 *
 * <h3>Production hygiene</h3>
 *
 * <ul>
 *   <li>Search is debounced (300 ms) so typing doesn't fan out a
 *       request per keystroke.</li>
 *   <li>Empty search falls back to the paginated list endpoint
 *       (first page, 50 entries) so the operator sees the catalog
 *       even with no query typed.</li>
 *   <li>Per-row install state — only the row being installed shows
 *       its spinner; other rows stay clickable.</li>
 *   <li>Already-installed entries render with a muted "Installed"
 *       pill instead of an Install button so the operator can't
 *       double-install (the backend is idempotent but the affordance
 *       should match the actual op).</li>
 *   <li>Errors surface inline next to the row that failed; the
 *       slideout chrome stays interactive.</li>
 *   <li>onInstalled fires with the API response so the parent can
 *       reload the agent and see the new Skill row.</li>
 * </ul>
 *
 * <h3>Props</h3>
 * <ul>
 *   <li>{@code isOpen}, {@code onClose} — slideout visibility.</li>
 *   <li>{@code agentId} — UUID of the agent receiving the install.</li>
 *   <li>{@code agentName} — display label for the slideout header.</li>
 *   <li>{@code onInstalled} — fired after a successful install.</li>
 * </ul>
 */
import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Library, Search, Plus, CheckCircle, Loader2, AlertTriangle, Package,
} from 'lucide-react';
import { Slideout } from '../Slideout';
import { Button } from '../Button';
import { Card } from '../Card';
import { Pill } from '../Pill';
import {
  listSkillRegistry, searchSkillRegistry, installSkill,
} from '../../../services/api';

const INPUT_CLASS =
  'v2-focus-ring w-full px-2.5 py-1.5 rounded-v2-sm border border-v2-border ' +
  'bg-v2-surface text-v2-ink text-[13px]';

const RISK_TONE = {
  low: 'muted', medium: 'warning', high: 'danger', critical: 'danger',
};

function debounce(fn, ms) {
  let h;
  return (...args) => {
    clearTimeout(h);
    h = setTimeout(() => fn(...args), ms);
  };
}

export function AddExistingSkillSlideout({
  isOpen, onClose, agentId, agentName, onInstalled,
}) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Per-row install state — keyed by registry entry id
  const [installingId, setInstallingId] = useState(null);
  const [rowErrors, setRowErrors] = useState({});      // id → message
  const [installedNow, setInstalledNow] = useState(new Set()); // ids installed in this session

  /* Refresh the catalog. Search path when the operator typed a query;
   * paginated list when the box is empty. Both yield an `items` array
   * the table renders directly. */
  const refresh = useCallback(async (q) => {
    setLoading(true);
    setError(null);
    let r;
    if (q && q.trim()) {
      r = await searchSkillRegistry({ query: q.trim(), k: 50 });
    } else {
      r = await listSkillRegistry({ page: 0, size: 50 });
    }
    if (r?.ok === false) {
      setError(r.error || 'Failed to load skill registry');
      setItems([]);
      setLoading(false);
      return;
    }
    // Both endpoints return { items: [...] }. The /search endpoint
    // wraps each hit with a `score` field but the inner shape is
    // the same MarketplaceJson DTO.
    const arr = Array.isArray(r?.items) ? r.items
              : Array.isArray(r) ? r
              : [];
    setItems(arr);
    setLoading(false);
  }, []);

  /* Debounced search trigger. The debounce wrapper holds across
   * renders via useRef so each keystroke doesn't reset the timer
   * (which would defeat the debounce entirely). */
  const debouncedRefreshRef = useRef(null);
  useEffect(() => {
    debouncedRefreshRef.current = debounce(refresh, 300);
  }, [refresh]);

  // First load + refresh on query change
  useEffect(() => {
    if (!isOpen) return;
    if (!query.trim()) {
      // Empty query → immediate paginated list (no debounce wait)
      refresh('');
    } else {
      // Non-empty → debounced search
      debouncedRefreshRef.current?.(query);
    }
  }, [isOpen, query, refresh]);

  // Reset state on each open so a previous error / install flash
  // doesn't leak into a fresh session.
  useEffect(() => {
    if (!isOpen) return;
    setQuery('');
    setRowErrors({});
    setInstalledNow(new Set());
    setInstallingId(null);
  }, [isOpen]);

  const onInstall = useCallback(async (entry) => {
    if (!entry?.id || !agentId) return;
    setInstallingId(entry.id);
    setRowErrors((prev) => { const n = { ...prev }; delete n[entry.id]; return n; });
    const r = await installSkill(entry.id, { agentId });
    if (r?.ok === false) {
      setRowErrors((prev) => ({ ...prev, [entry.id]: r.error || 'Install failed' }));
      setInstallingId(null);
      return;
    }
    setInstalledNow((prev) => {
      const n = new Set(prev);
      n.add(entry.id);
      return n;
    });
    setInstallingId(null);
    if (typeof onInstalled === 'function') onInstalled(r, entry);
  }, [agentId, onInstalled]);

  return (
    <Slideout
      isOpen={isOpen}
      onClose={onClose}
      ariaLabel={agentName
        ? `Add existing skill to ${agentName}`
        : 'Add existing skill'}
      width={720}
    >
      <div className="p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Library size={16} className="text-v2-action-strong" />
          <h2 className="text-[15px] font-semibold text-v2-ink m-0">
            Add existing skill from registry
          </h2>
          {agentName && (
            <Pill tone="muted" size="sm">→ {agentName}</Pill>
          )}
        </div>

        <p className="text-[12px] text-v2-ink-secondary leading-relaxed m-0">
          Pick a skill from your tenant's shared registry catalog and install it on
          this agent. Re-installing a skill the agent already has is safe and has no
          effect. Use the search box to filter by name, description, or category.
        </p>

        <label className="block">
          <span className="block text-[10.5px] font-semibold uppercase tracking-wider text-v2-ink-muted mb-1">
            <Search size={10} style={{ display: 'inline', marginRight: 4 }} />
            Search
          </span>
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="e.g. fleet-controller, evidence-collector, soc-triage…"
            className={INPUT_CLASS}
          />
        </label>

        {error && (
          <div className="px-3 py-2 rounded-v2-sm bg-v2-danger-soft border border-v2-danger/30 text-[12px] text-v2-danger-strong flex items-center gap-2">
            <AlertTriangle size={13} /> {error}
          </div>
        )}

        {loading ? (
          <div className="px-3 py-6 rounded-v2-sm bg-v2-surface-muted/30 text-[12px] text-v2-ink-muted flex items-center justify-center gap-2">
            <Loader2 size={14} className="animate-spin" /> Loading catalog…
          </div>
        ) : items.length === 0 ? (
          <div className="px-3 py-6 rounded-v2-sm bg-v2-surface-muted/30 text-[12px] text-v2-ink-muted flex items-center justify-center gap-2">
            <Package size={14} />
            {query.trim()
              ? `No matches for "${query.trim()}" in your registry.`
              : 'No skills in this tenant\'s registry catalog yet.'}
          </div>
        ) : (
          <Card padding="sm">
            <Card.Body>
              <ul className="divide-y divide-v2-border-subtle">
                {items.map((entry) => {
                  const id = entry.id;
                  const alreadyInstalled = entry.installed === true
                    || installedNow.has(id);
                  const isInstalling = installingId === id;
                  const rowError = rowErrors[id];
                  return (
                    <li key={id} className="py-2.5 flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-v2-ink text-[13px] truncate">
                            {entry.displayName || entry.operationId || id}
                          </span>
                          {entry.kind && (
                            <Pill tone="muted" size="sm">{entry.kind}</Pill>
                          )}
                          {entry.riskLevel && (
                            <Pill tone={RISK_TONE[entry.riskLevel] || 'muted'} size="sm">
                              {entry.riskLevel}
                            </Pill>
                          )}
                          {entry.category && (
                            <span className="text-[10.5px] text-v2-ink-muted">
                              {entry.category}
                            </span>
                          )}
                        </div>
                        {entry.description && (
                          <p className="text-[11.5px] text-v2-ink-secondary mt-0.5 leading-snug m-0 line-clamp-2">
                            {entry.description}
                          </p>
                        )}
                        <code className="font-mono-v2 text-[10.5px] text-v2-ink-muted block mt-1 truncate" title={id}>
                          {id}
                        </code>
                        {rowError && (
                          <div className="mt-1.5 text-[10.5px] text-v2-danger-strong flex items-start gap-1">
                            <AlertTriangle size={11} className="mt-0.5 shrink-0" />
                            <span>{rowError}</span>
                          </div>
                        )}
                      </div>
                      <div className="shrink-0">
                        {alreadyInstalled ? (
                          <Pill tone="success" size="sm" dot>
                            <CheckCircle size={11} style={{ display: 'inline', marginRight: 4 }} />
                            installed
                          </Pill>
                        ) : (
                          <Button
                            variant="primary"
                            size="sm"
                            disabled={isInstalling}
                            iconLeft={isInstalling
                              ? <Loader2 size={12} className="animate-spin" />
                              : <Plus size={12} />}
                            onClick={() => onInstall(entry)}
                          >
                            {isInstalling ? 'Installing…' : 'Install'}
                          </Button>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card.Body>
          </Card>
        )}

        <div className="flex items-center justify-end pt-2 border-t border-v2-border-subtle">
          <Button variant="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>
      </div>
    </Slideout>
  );
}

export default AddExistingSkillSlideout;
